import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldAlert, Sparkles, RefreshCw, AlertTriangle } from "lucide-react";

const verdictBadge = (v) => {
  if (v === "خطر") return "bg-red-100 text-red-800 border-red-300";
  if (v === "مشبوه") return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-emerald-100 text-emerald-800 border-emerald-300";
};

export default function FraudDetection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const runScan = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/ai/return-fraud-detect?days=30");
      setData(d);
      if (d.count === 0) toast.success("لا توجد أنماط إرجاع مشبوهة");
      else toast.warning(`تم رصد ${d.count} عميلاً مشبوهاً`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { runScan(); }, []); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="fraud-detection-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-red-600" /> رصد أنماط الإرجاع المشبوهة
          </h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            تحليل ذكي بالـ AI لعملاء يُعيدون بشكل غير طبيعي (5+ مرات أو &gt;40%)
          </p>
        </div>
        <Button onClick={runScan} disabled={busy} className="gap-1 bg-red-600 hover:bg-red-700" data-testid="rescan-btn">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
          {busy ? "جاري الفحص..." : "إعادة الفحص"}
        </Button>
      </div>

      {data?.ai_summary && (
        <Card className="p-4 bg-gradient-to-r from-violet-50 to-blue-50 dark:from-violet-950/20 dark:to-blue-950/20 border-violet-200" data-testid="ai-summary-card">
          <div className="flex items-start gap-2">
            <Sparkles className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-xs text-violet-700 font-bold">ملخص الذكاء الاصطناعي</div>
              <div className="text-sm mt-1">{data.ai_summary}</div>
            </div>
          </div>
        </Card>
      )}

      {!data ? null : data.count === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <ShieldAlert className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
          <div className="text-emerald-700 font-bold">لا توجد أنماط مشبوهة</div>
          <div className="text-xs text-muted-foreground/70 mt-1">جميع العملاء ضمن المعدلات الطبيعية للإرجاع (آخر {data.period_days} يوماً)</div>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.suspicious.map(s => (
            <Card key={s.id} className={`p-4 border ${s.ai_verdict === "خطر" ? "border-red-300 bg-red-50/40" : s.ai_verdict === "مشبوه" ? "border-amber-300 bg-amber-50/40" : ""}`} data-testid={`suspicious-${s.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-bold">{s.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{s.sales_count} فاتورة · {s.returns_count} مرتجع</div>
                </div>
                {s.ai_verdict && <span className={`text-[10px] px-2 py-0.5 rounded-full border ${verdictBadge(s.ai_verdict)}`}>{s.ai_verdict}</span>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div>
                  <div className="text-muted-foreground">قيمة المرتجعات</div>
                  <div className="font-bold text-amber-700 tabular-nums">{s.returns_total.toLocaleString("ar-EG")}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">النسبة من المبيعات</div>
                  <div className={`font-bold tabular-nums ${s.ratio > 40 ? "text-red-600" : "text-amber-600"}`}>{s.ratio}%</div>
                </div>
              </div>
              {s.ai_actions?.length > 0 && (
                <div className="border-t pt-2 mt-2">
                  <div className="text-[10px] text-muted-foreground font-bold flex items-center gap-1 mb-1">
                    <AlertTriangle className="w-3 h-3 text-amber-500" /> الإجراءات المقترحة
                  </div>
                  <ul className="text-xs space-y-1 list-disc list-inside text-muted-foreground">
                    {s.ai_actions.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
